/**
 * "מה קורה אחרי שליחה" — one behaviour for every public surface that ends in a
 * submission: forms / landing pages (/p/:slug → PublicTool) and campaigns (the
 * thank-you page /payments/complete, plus the campaign page's own
 * "already submitted" gate). Stored as `afterSubmit` on Tool and Campaign
 * (backend/src/models/afterSubmitSchema.ts).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';

export type AfterSubmitMode = 'message' | 'redirect' | 'message_redirect' | 'exit';
export type ResubmitMode = 'none' | 'device' | 'field';
export type MatchField = 'phone' | 'email' | 'idNumber';

export interface AfterSubmitConfig {
  mode: AfterSubmitMode;
  title: string;
  message: string;
  buttonText: string;
  buttonUrl: string;
  redirectUrl: string;
  redirectDelaySec: number;
  exitUrl: string;
  preventResubmit: ResubmitMode;
  matchField: MatchField;
  alreadySubmittedMessage: string;
}

export const DEFAULT_AFTER_SUBMIT: AfterSubmitConfig = {
  mode: 'message',
  title: '',
  message: '',
  buttonText: '',
  buttonUrl: '',
  redirectUrl: '',
  redirectDelaySec: 3,
  exitUrl: '',
  preventResubmit: 'none',
  matchField: 'phone',
  alreadySubmittedMessage: '',
};

export const DEFAULT_ALREADY_SUBMITTED = 'כבר מילאת את הטופס הזה. תודה!';

// Model defaults — a doc that still carries one of these never had its
// message customised, so a value set elsewhere wins over it.
const STOCK_TOOL_MESSAGES = new Set(['תודה! הטופס התקבל בהצלחה.', 'תודה! הטופס נשלח בהצלחה', '']);

/**
 * Effective config for a tool. `afterSubmit` wins when present; otherwise the
 * legacy fields: Tool.successMessage / redirectUrl (classic editor) and
 * generalSettings.thankYouMessage / redirectAfterSubmit (the /tools/new
 * editor, which used to save them without anything reading them).
 */
export function resolveToolAfterSubmit(tool: any): { cfg: AfterSubmitConfig; custom: boolean } {
  if (tool?.afterSubmit && typeof tool.afterSubmit === 'object') {
    return { cfg: { ...DEFAULT_AFTER_SUBMIT, ...tool.afterSubmit }, custom: true };
  }
  const gs = tool?.generalSettings || {};
  const own = String(tool?.successMessage || '').trim();
  const v3 = String(gs.thankYouMessage || '').trim();
  const message = !STOCK_TOOL_MESSAGES.has(own) ? own : (!STOCK_TOOL_MESSAGES.has(v3) ? v3 : own);
  const redirectUrl = String(tool?.redirectUrl || gs.redirectAfterSubmit || '').trim();
  return {
    cfg: { ...DEFAULT_AFTER_SUBMIT, message, redirectUrl, mode: redirectUrl ? 'message_redirect' : 'message', redirectDelaySec: 1.5 },
    custom: false,
  };
}

export function isSafeUrl(url: string): boolean {
  const u = (url || '').trim();
  if (!u) return false;
  if (u.startsWith('/') && !u.startsWith('//')) return true;
  return /^https?:\/\/\S+$/i.test(u);
}

// ── merge fields ─────────────────────────────────────────────────────────────

/** Label → value map for {שדה} placeholders, plus a few stable aliases. */
export function buildToolMergeVars(tool: any, data: any): Record<string, string> {
  const vars: Record<string, string> = {};
  if (!data || typeof data !== 'object') return vars;
  const flat: Record<string, any> = tool?.type === 'signature' ? (data.values || {}) : data;
  const fields: any[] = [
    ...(((tool?.blocks as any[]) || []).filter(b => b?.type === 'form').flatMap(b => b?.props?.formFields || [])),
    ...((tool?.fields as any[]) || []),
    ...((tool?.inlineFields as any[]) || []),
  ];
  for (const f of fields) {
    if (!f?.id) continue;
    const v = flat[f.id];
    if (v === undefined || v === null || typeof v === 'object') continue;
    const s = String(v).trim();
    if (!s || s.startsWith('data:')) continue;
    if (f.label) vars[String(f.label).trim()] = s;
    vars[String(f.id)] = s;
    const label = String(f.label || '').toLowerCase();
    const type = String(f.type || '');
    if (!vars.name && (label.includes('שם מלא') || label === 'שם' || label === 'name' || label === 'full name')) vars.name = s;
    if (!vars.firstName && (label.includes('שם פרטי') || label.includes('first name'))) vars.firstName = s;
    if (!vars.phone && (type === 'tel' || type === 'phone' || label.includes('טלפון') || label.includes('נייד'))) vars.phone = s;
    if (!vars.email && (type === 'email' || label.includes('מייל'))) vars.email = s;
  }
  if (!vars.name && vars.firstName) vars.name = vars.firstName;
  if (!vars.firstName && vars.name) vars.firstName = vars.name.split(/\s+/)[0];
  // Hebrew aliases so the editor's chips read naturally.
  if (vars.name) vars['שם'] = vars['שם'] || vars.name;
  if (vars.firstName) vars['שם פרטי'] = vars['שם פרטי'] || vars.firstName;
  return vars;
}

/** Replaces {key} with vars[key]; unknown keys become empty. `encode` for URLs. */
export function fillMergeFields(template: string, vars: Record<string, string>, encode = false): string {
  if (!template) return '';
  return template.replace(/\{([^{}]{1,60})\}/g, (_m, key: string) => {
    const v = vars[key.trim()] ?? '';
    return encode ? encodeURIComponent(v) : v;
  });
}

// ── "already submitted" memory ───────────────────────────────────────────────

const storeKey = (kind: 'tool' | 'campaign', slug: string) => `amt_submitted_${kind}_${slug}`;

/** device → forever on this browser; exit → for this tab session only (back / refresh). */
export function rememberSubmitted(kind: 'tool' | 'campaign', slug: string, cfg: AfterSubmitConfig) {
  if (!slug) return;
  try {
    if (cfg.preventResubmit === 'device') localStorage.setItem(storeKey(kind, slug), String(Date.now()));
    if (cfg.mode === 'exit') sessionStorage.setItem(storeKey(kind, slug), String(Date.now()));
  } catch { /* storage blocked — the gate is best-effort */ }
}

export function wasSubmitted(kind: 'tool' | 'campaign', slug: string, cfg: AfterSubmitConfig): 'device' | 'exit' | null {
  if (!slug) return null;
  try {
    if (cfg.preventResubmit === 'device' && localStorage.getItem(storeKey(kind, slug))) return 'device';
    if (cfg.mode === 'exit' && sessionStorage.getItem(storeKey(kind, slug))) return 'exit';
  } catch { /* ignore */ }
  return null;
}

// ── screens ──────────────────────────────────────────────────────────────────

const CARD_CSS = `
@keyframes amtAsPop { 0% { opacity: 0; transform: translateY(14px) scale(.96) } 100% { opacity: 1; transform: none } }
@media (prefers-reduced-motion: reduce) { .amt-as-card { animation: none !important } }
`;

const Shell: React.FC<{ primary: string; logoUrl?: string; children: React.ReactNode }> = ({ primary, logoUrl, children }) => (
  <div dir="rtl" style={{
    minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    background: `linear-gradient(135deg, ${primary}14 0%, #ffffff 45%, ${primary}0A 100%)`,
    fontFamily: "'Heebo', sans-serif",
  }}>
    <style>{CARD_CSS}</style>
    <div className="amt-as-card" style={{
      width: '100%', maxWidth: 440, background: '#fff', borderRadius: 24, padding: '34px 26px 28px',
      boxShadow: '0 24px 60px rgba(15,23,42,.16)', textAlign: 'center',
      animation: 'amtAsPop .3s cubic-bezier(.16,1,.3,1) both',
    }}>
      {logoUrl && <img src={logoUrl} alt="לוגו" style={{ height: 44, objectFit: 'contain', margin: '0 auto 16px', display: 'block' }} />}
      {children}
    </div>
  </div>
);

const Check: React.FC<{ primary: string }> = ({ primary }) => (
  <div style={{
    width: 76, height: 76, borderRadius: '50%', margin: '0 auto 18px', display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: `linear-gradient(135deg, ${primary}, ${primary}BB)`, boxShadow: `0 10px 26px ${primary}55`,
  }}>
    <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round"><path d="M5 13l4 4L19 7" /></svg>
  </div>
);

/** Plain text with line breaks and bare links — no HTML injection. */
const RichText: React.FC<{ text: string }> = ({ text }) => (
  <p style={{ fontSize: 15.5, color: '#4b5563', lineHeight: 1.75, margin: 0, whiteSpace: 'pre-line' }}>
    {text.split(/(https?:\/\/[^\s]+)/g).map((part, i) =>
      /^https?:\/\//.test(part)
        ? <a key={i} href={part} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', wordBreak: 'break-all' }}>{part}</a>
        : <React.Fragment key={i}>{part}</React.Fragment>)}
  </p>
);

/**
 * The final screen. Owns the side effects of the chosen mode: redirect after a
 * countdown, or — for 'exit' — lock the back button, try to close the tab and
 * optionally bounce out to exitUrl. `extra` renders under the message (e.g.
 * the payment details on the campaign thank-you page).
 */
export const AfterSubmitScreen: React.FC<{
  cfg: AfterSubmitConfig;
  vars: Record<string, string>;
  primary?: string;
  logoUrl?: string;
  defaultTitle?: string;
  defaultMessage?: string;
  extra?: React.ReactNode;
  /** Floor for any redirect — e.g. the thank-you page lets the Meta pixel fire first. */
  minDelaySec?: number;
}> = ({ cfg, vars, primary = '#1565C0', logoUrl, defaultTitle = 'הטופס נשלח בהצלחה!', defaultMessage = 'תודה! הפרטים התקבלו אצלנו.', extra, minDelaySec = 0 }) => {
  const title = fillMergeFields(cfg.title, vars) || defaultTitle;
  const message = fillMergeFields(cfg.message, vars) || defaultMessage;
  const redirectTo = useMemo(() => {
    const raw = cfg.mode === 'exit' ? cfg.exitUrl : (cfg.mode === 'redirect' || cfg.mode === 'message_redirect') ? cfg.redirectUrl : '';
    const url = fillMergeFields(raw, vars, true);
    return isSafeUrl(url) ? url : '';
  }, [cfg, vars]);
  const delay = Math.max(minDelaySec, cfg.mode === 'redirect' ? 0 : Math.max(0, Number(cfg.redirectDelaySec) || 0));
  const [secondsLeft, setSecondsLeft] = useState(Math.ceil(delay));
  const [closeFailed, setCloseFailed] = useState(false);
  const buttonUrl = fillMergeFields(cfg.buttonUrl, vars, true);
  const firedRef = useRef(false);

  // Redirect (replace — "back" must not land on the form again for exit).
  useEffect(() => {
    if (!redirectTo || firedRef.current) return;
    const go = () => {
      if (firedRef.current) return;
      firedRef.current = true;
      if (cfg.mode === 'exit') window.location.replace(redirectTo);
      else window.location.href = redirectTo;
    };
    if (delay <= 0) { go(); return; }
    const started = Date.now();
    const t = window.setInterval(() => {
      const left = Math.ceil(delay - (Date.now() - started) / 1000);
      setSecondsLeft(Math.max(0, left));
      if (left <= 0) { window.clearInterval(t); go(); }
    }, 250);
    return () => window.clearInterval(t);
  }, [redirectTo, delay, cfg.mode]);

  // Exit: pin the back button to this screen and try to close the tab. Only a
  // tab the page opened itself can be closed by script, so this mostly works
  // in the WhatsApp / Facebook in-app browsers; elsewhere we say so.
  useEffect(() => {
    if (cfg.mode !== 'exit') return;
    try { window.history.pushState({ amtExit: true }, '', window.location.href); } catch { /* ignore */ }
    const onPop = () => { try { window.history.pushState({ amtExit: true }, '', window.location.href); } catch { /* ignore */ } };
    window.addEventListener('popstate', onPop);
    const t = redirectTo ? 0 : window.setTimeout(() => {
      try { window.close(); } catch { /* ignore */ }
      window.setTimeout(() => setCloseFailed(true), 400);
    }, Math.max(1200, delay * 1000));
    return () => { window.removeEventListener('popstate', onPop); if (t) window.clearTimeout(t); };
  }, [cfg.mode, redirectTo, delay]);

  if (cfg.mode === 'redirect' && redirectTo) {
    return (
      <Shell primary={primary} logoUrl={logoUrl}>
        <div style={{ width: 44, height: 44, borderRadius: '50%', border: `4px solid ${primary}33`, borderTopColor: primary, margin: '8px auto 14px', animation: 'spin 0.9s linear infinite' }} />
        <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
        <p style={{ color: '#6b7280', margin: 0 }}>מעבירים אתכם…</p>
      </Shell>
    );
  }

  return (
    <Shell primary={primary} logoUrl={logoUrl}>
      <Check primary={primary} />
      <h1 style={{ fontSize: 24, fontWeight: 900, color: '#111827', margin: '0 0 10px' }}>{title}</h1>
      <RichText text={message} />
      {extra}
      {cfg.buttonText && isSafeUrl(buttonUrl) && cfg.mode !== 'exit' && (
        <a href={buttonUrl} style={{
          display: 'block', marginTop: 22, padding: '13px 20px', borderRadius: 14, background: primary, color: '#fff',
          fontSize: 15.5, fontWeight: 800, textDecoration: 'none', boxShadow: `0 8px 20px ${primary}44`,
        }}>{cfg.buttonText}</a>
      )}
      {redirectTo && secondsLeft > 0 && (
        <p style={{ fontSize: 13, color: '#9ca3af', marginTop: 18 }}>מעבירים אתכם בעוד {secondsLeft} שניות…</p>
      )}
      {cfg.mode === 'exit' && !redirectTo && (
        <p style={{ fontSize: 13, color: '#9ca3af', marginTop: 18 }}>
          {closeFailed ? 'סיימתם. אפשר לסגור את החלון.' : 'החלון ייסגר בעוד רגע…'}
        </p>
      )}
    </Shell>
  );
};

export const AlreadySubmittedScreen: React.FC<{ cfg: AfterSubmitConfig; primary?: string; logoUrl?: string; reason: 'device' | 'exit' }> = ({ cfg, primary = '#1565C0', logoUrl, reason }) => {
  // After an exit, back/refresh shows the same final screen — not a fresh form.
  if (reason === 'exit') return <AfterSubmitScreen cfg={{ ...cfg, exitUrl: '' }} vars={{}} primary={primary} logoUrl={logoUrl} />;
  return (
    <Shell primary={primary} logoUrl={logoUrl}>
      <Check primary={primary} />
      <RichText text={cfg.alreadySubmittedMessage || DEFAULT_ALREADY_SUBMITTED} />
    </Shell>
  );
};
